import { Suspense, lazy, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@features/auth/auth.store.ts';
import { useActiveWebViewStore, useIsWebViewActive } from '@shared/state/active-webview.ts';
import { AssistantFab } from './AssistantFab.tsx';
import { isAssistantHiddenOn } from '../assistant.constants.ts';
import { pickAssistantDock } from '../assistant-dock.ts';

/**
 * El panel viaja en su propio chunk y sólo se descarga al abrir el asistente.
 *
 * Importarlo de forma estática metía echarts —378 KB comprimidos— en la carga inicial de
 * todas las rutas, porque el lanzador se monta siempre y el panel dibuja gráficas en las
 * respuestas. El botón, que es lo único visible de entrada, no necesita nada de eso.
 */
const AssistantPanel = lazy(async () => ({
  default: (await import('./AssistantPanel.tsx')).AssistantPanel,
}));

interface AssistantLauncherProps {
  /** La ruta actual. Llega por props porque este componente vive fuera del router. */
  pathname: string;
  /**
   * Lleva a la página completa del asistente (`/asistente`).
   *
   * Navega y no expande in-place porque este componente vive fuera de `<RouterProvider>` y no
   * puede usar `useNavigate()`; quien sí conoce el router es `AssistantMount`, que lo inyecta
   * aquí y de aquí baja hasta el botón que lo dispara.
   */
  onExpand: () => void;
}

/**
 * El asistente flotante: el botón y, si está abierto, el panel.
 *
 * Se monta una sola vez para todo el portal. La ruta llega por props y no de `useLocation`
 * porque el punto de montaje está fuera de `<RouterProvider>` — es el único sitio que cubre las
 * veintisiete rutas sin tocar las veinte vistas, que en su mayoría no comparten shell.
 *
 * El panel se desmonta al cerrar, pero la conversación no se pierde: vive en `assistant.store.ts`,
 * un singleton de Zustand que sigue en memoria y que además se va guardando en el backend, así
 * que reabrir el panel —o entrar a `/asistente`— la encuentra donde la dejó.
 */
export function AssistantLauncher({ pathname, onExpand }: AssistantLauncherProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Para las iniciales de sus mensajes en el mini-chat. Se lee aquí, que ya consulta la sesión,
  // y baja al panel por props.
  const userName = useAuthStore((state) => state.user?.name_user);
  const [open, setOpen] = useState(false);
  const [thinking, setThinking] = useState(false);

  // Dónde se incrusta el botón lo decide `pickAssistantDock`: la barra de control con un mapa
  // delante, la barra de la tabla dinámica si no, y la esquina si la vista no ofrece ninguna.
  const webViewActive = useIsWebViewActive();
  const dockNodes = useActiveWebViewStore((state) => state.dockNodes);
  const dockNode = pickAssistantDock(webViewActive, dockNodes);
  const docked = dockNode !== null;

  // `useCallback` porque el panel lo tiene en las dependencias de un efecto: sin estabilizarlo,
  // avisaría del estado en cada render.
  const handleThinkingChange = useCallback((value: boolean) => setThinking(value), []);

  if (!isAuthenticated || isAssistantHiddenOn(pathname)) return null;

  const fab = (
    <AssistantFab
      open={open}
      thinking={thinking && !open}
      docked={docked}
      onClick={() => setOpen((value) => !value)}
    />
  );

  return (
    <>
      {open && (
        // Sin `fallback` visible: el chunk tarda milisegundos y un esqueleto que aparece
        // y desaparece se lee como un parpadeo.
        <Suspense fallback={null}>
          <AssistantPanel
            anchorNode={dockNode}
            userName={userName}
            onClose={() => setOpen(false)}
            onExpand={onExpand}
            onThinkingChange={handleThinkingChange}
          />
        </Suspense>
      )}
      {dockNode ? createPortal(fab, dockNode) : fab}
    </>
  );
}
